// @ts-nocheck
/**
 * Final inline card that renders the agent's structured mapping at the
 * end of a run. Data comes from the backend's mapping_complete SSE event
 * (assembled deterministically in agent/runtime.py:_assemble_final_mapping)
 * — the LLM no longer transcribes a JSON block.
 *
 * Visual language matches the existing tool-call renderers
 * (toolRenderers.tsx) and NetProfitBreakdown: same CSS variables, same
 * accounting number formatting, hand-rolled to fit the app's design
 * system rather than introducing a component library.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Copy,
  Check,
  Save,
  FileSpreadsheet,
  Loader2,
  ChevronUp,
} from 'lucide-react';

import {
  downloadAgentMappingAsExcel,
  SourceFileMissingError,
} from '../../utils/exportExcel';
import {
  patchAgentMapping,
  getProvenance,
  getAgentInsights,
  getAgentEbitdaBridge,
} from '../../utils/agentApi';
import { prefetchAnalyses } from '../../utils/analysisPrefetch';
import { getInflightJob } from '../../utils/generationJobs';
import CellReferencesToggle from './CellReferencesToggle';
import { useTaxonomy } from './taxonomyContext';
import PeriodPicker from './PeriodPicker';
import RatiosTab from './RatiosTab';
import InsightsTab from './InsightsTab';
import EBITDABridgeTab from './EBITDABridgeTab';
import TieOutTab from './TieOutTab';
import TrustBadge from './TrustBadge';

const STATUS_META = {
  pass: { label: 'Reconciled', tone: 'good', icon: CheckCircle },
  converged: { label: 'Reconciled', tone: 'good', icon: CheckCircle },
  exhausted: { label: 'Partial', tone: 'warn', icon: AlertTriangle },
  error: { label: 'Error', tone: 'bad', icon: XCircle },
  unknown: { label: 'No verify', tone: 'muted', icon: AlertTriangle },
};

const fmtCell = (n) => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `(${formatted})` : formatted;
};

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

// value_by_period is either a positional array aligned to periods[] (new
// backend shape) or a legacy int/str-keyed dict. `pos` is the period's index
// within periods[] (mapping.periods verbatim) and is only needed for the
// array form; the dict form keys directly by period_index.
const periodKey = (acct, pidx, pos) => {
  const vbp = acct.value_by_period;
  if (Array.isArray(vbp)) return (pos == null ? null : vbp[pos]) ?? null;
  return vbp?.[pidx] ?? vbp?.[String(pidx)] ?? null;
};

// `embedded` renders the card as a bare mapping panel for composition inside
// the unified mega-card: no outer header, no tab row (the host owns tabs and
// mounts Trends / Insights / Bridge / Tie-Out itself against the right
// session ids). Everything else — grouped table, category edits + save,
// reconciliation table, export popover — behaves identically.
const FinalMappingCard = ({
  mapping,
  sessionId,
  onMappingUpdated,
  embedded = false,
  runComplete = false,
}) => {
  // Selectable categories, grouping, ordering and gradients. The standard
  // P&L constants unless a host mounted a TaxonomyProvider (Databook).
  const { pnl: taxonomy } = useTaxonomy();
  const [copied, setCopied] = useState(false);
  // 'mapping' (table + export) | 'trends' (P&L ratios) | 'insights' | 'tieout'.
  const [activeTab, setActiveTab] = useState('mapping');
  // Tie-out provenance ledger — fetched once and shared by the header
  // TrustBadge and the Tie-Out tab so opening the tab never re-requests.
  const [provLoading, setProvLoading] = useState(true);
  const [provData, setProvData] = useState(null);
  const [provError, setProvError] = useState(null);
  // Tie-Out tab filter; the TrustBadge can deep-link to 'flagged'.
  const [tieFilter, setTieFilter] = useState('all');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Non-error outcome note for the export (e.g. a ticked analysis sheet
  // was skipped because its artifact went missing, or the P&L's
  // adjusted-EBITDA block was left out for lack of approved add-backs).
  const [downloadNotice, setDownloadNotice] = useState(null);
  // Default ON — agent users get the linked variant (source sheet
  // embedded, populated cells reference it) unless they opt out. When
  // the source file is gone from /tmp the download handler flips this
  // back off automatically and surfaces a hint so a retry succeeds.
  const [enableReferencing, setEnableReferencing] = useState(true);
  // Per-tab "export as a sheet" checkboxes. The analyses generate
  // automatically at the end of a run (prefetchAnalyses below), so the
  // workbook ships them by default: Trends starts ticked (the server
  // computes it on demand) and Insights / Bridge tick themselves when the
  // panel's availability check finds their artifact. Unticking is a
  // per-download opt-out.
  const [sheetOptions, setSheetOptions] = useState({
    trends: true,
    insights: false,
    bridge: false,
  });
  // Availability of the gated artifacts, refreshed each time the export
  // panel opens: 'unknown' | 'checking' | 'available' | 'generating'
  // (a tab's auto-run is still in flight) | 'missing'.
  const [sheetAvail, setSheetAvail] = useState({
    insights: 'unknown',
    bridge: 'unknown',
  });
  // Export options live in a glass popover that pops out to the *right*
  // of the Download trigger — keeps the resting state of the card clean.
  // The state machine has three flags so we can run independent open AND
  // close animations:
  //   - exportPanelOpen     : user intent (true after click, false after dismiss)
  //   - exportPanelMounted  : panel is in the DOM (stays true during close anim)
  //   - exportPanelVisible  : panel has the `is-open` class (drives CSS transitions)
  // Open path : open=true → mounted=true → next frame visible=true → CSS open transition.
  // Close path: open=false → visible=false → CSS close transition → 240ms → mounted=false.
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [exportPanelMounted, setExportPanelMounted] = useState(false);
  const [exportPanelVisible, setExportPanelVisible] = useState(false);
  // Panel uses position:fixed to escape .fmc-card's overflow:hidden, so we
  // pin it to the trigger's right-edge each time the trigger moves
  // (scroll, resize, panel reopen). `top` is clamped so the panel never
  // extends past the viewport; `notch` is the px offset from the panel's
  // top to the trigger's centre so the arrow keeps pointing at the
  // trigger even when clamping shifts the panel off-centre.
  const [triggerPos, setTriggerPos] = useState({ top: 0, left: 0, notch: null });
  const exportPanelRef = useRef(null);
  const exportTriggerRef = useRef(null);
  // Local category overrides keyed by row_index. ``handleSave`` POSTs
  // them to the backend, which writes them into ``view_state`` and
  // returns the recomputed mapping (reconciliation + period_breakdowns
  // included). On success the caller swaps the new mapping into chat
  // items via ``onMappingUpdated``; from that point ``downloadMappingAsExcel``
  // reflects the user's intent because it reads from the mapping prop.
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const hasEdits = Object.keys(edits).length > 0;

  const periods = mapping?.periods || [];
  const accounts = mapping?.accounts || [];
  const reconciliation = mapping?.reconciliation || { status: 'unknown' };
  const periodBreakdowns = mapping?.period_breakdowns || [];

  // Period selection for the export. Defaults to every primary column on
  // first render; the user can toggle individual columns or use the group
  // buttons on the PeriodPicker. Falls back to "everything except empty"
  // for legacy sessions whose payload predates the classification field
  // so the picker isn't useless on resumed runs. If the mapping comes back
  // with a different period set (e.g. a continuation turn), the effect
  // below resets the selection so the picker tracks the new structure.
  const computeDefaultSelection = (ps) => {
    if (!ps || ps.length === 0) return [];
    const primaries = ps
      .filter((p) => (p.classification ?? 'unknown') === 'primary')
      .map((p) => p.period_index);
    if (primaries.length > 0) return primaries;
    const nonEmpty = ps
      .filter((p) => (p.classification ?? 'unknown') !== 'empty')
      .map((p) => p.period_index);
    return nonEmpty;
  };
  const [selectedPeriodIndices, setSelectedPeriodIndices] = useState(() =>
    computeDefaultSelection(periods),
  );
  // Re-sync the default when the mapping's period set changes shape
  // (e.g. continuation turn produced a fresh mapping payload). Comparing
  // the joined indices is cheap and avoids identity churn on every render.
  const periodsKey = periods.map((p) => p.period_index).join(',');
  useEffect(() => {
    setSelectedPeriodIndices(computeDefaultSelection(periods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodsKey]);

  // Tie-out provenance: lazy-fetch once on mount (and after a save, which
  // invalidates the server cache). Shared with the header TrustBadge so the
  // green/red verdict is visible the moment the card renders, before any tab
  // is opened — the whole "they don't have to check" point.
  const loadProvenance = React.useCallback(() => {
    if (!sessionId) {
      setProvLoading(false);
      return undefined;
    }
    let active = true;
    setProvLoading(true);
    setProvError(null);
    getProvenance(sessionId)
      .then((d) => {
        if (active) {
          setProvData(d);
          setProvLoading(false);
        }
      })
      .catch((err) => {
        if (active) {
          setProvError(err.message || 'Could not load tie-out');
          setProvLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [sessionId]);

  useEffect(() => loadProvenance(), [loadProvenance]);

  // Kick off the analyses behind the tabs (Trends, Insights, EBITDA Bridge)
  // as soon as the run is over, so they land pre-computed instead of waiting
  // for a tab click. Skipped when embedded — the unified mega-card owns the
  // tabs there and prefetches against the right hosts itself (insights live
  // on the PARENT session, not this child).
  useEffect(() => {
    if (embedded || !runComplete || !sessionId) return;
    prefetchAnalyses({ pnlSessionId: sessionId, insightsSessionId: sessionId });
  }, [embedded, runComplete, sessionId]);

  const handleJumpToTieout = (filter) => {
    setActiveTab('tieout');
    setTieFilter(filter || 'all');
  };

  // The export trigger renders on both the Mapping and EBITDA Bridge tabs but
  // the popover is anchored to whichever trigger is mounted — close it (and
  // drop any stale outcome note) when the user switches tabs so it never
  // floats next to a trigger that no longer exists.
  useEffect(() => {
    setExportPanelOpen(false);
    setDownloadNotice(null);
  }, [activeTab]);

  // Dismiss the export popover on outside click / Escape. Anchored to the
  // trigger so a click on the trigger itself doesn't double-fire (the
  // trigger's onClick is what opened the panel in the first place).
  useEffect(() => {
    if (!exportPanelOpen) return;
    const onPointer = (e) => {
      const t = e.target;
      if (exportPanelRef.current && exportPanelRef.current.contains(t)) return;
      if (exportTriggerRef.current && exportTriggerRef.current.contains(t)) return;
      setExportPanelOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setExportPanelOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportPanelOpen]);

  // Open/close orchestration. Mount the panel before flipping `visible`
  // so the browser sees a "from" state for the CSS transition; on close,
  // flip `visible` off first and unmount after the animation lands.
  useEffect(() => {
    if (exportPanelOpen) {
      setExportPanelMounted(true);
      const r = requestAnimationFrame(() => setExportPanelVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setExportPanelVisible(false);
    const t = setTimeout(() => setExportPanelMounted(false), 240);
    return () => clearTimeout(t);
  }, [exportPanelOpen]);

  // Check which analysis artifacts exist each time the panel opens, so the
  // per-tab checkboxes reflect the server truth (both GETs are Redis-first
  // reads; null means not generated). The auto-run may still be in flight
  // (generationJobs registry) — show 'generating' and re-check when the job
  // settles so the row flips over while the panel is open. An available
  // artifact ticks its own box (the deliverable ships complete by default);
  // any non-available outcome unticks it, so a stale tick can never ship a
  // flag for a missing artifact.
  useEffect(() => {
    if (!exportPanelOpen || !sessionId) return undefined;
    let active = true;

    const clearOption = (key) =>
      setSheetOptions((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
    const tickOption = (key) =>
      setSheetOptions((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

    const check = (key, jobKey, fetchFn) => {
      const inflight = getInflightJob(jobKey);
      if (inflight) {
        setSheetAvail((prev) => ({ ...prev, [key]: 'generating' }));
        clearOption(key);
        const recheck = () => {
          if (active) check(key, jobKey, fetchFn);
        };
        inflight.then(recheck, recheck);
        return;
      }
      setSheetAvail((prev) => ({ ...prev, [key]: 'checking' }));
      fetchFn(sessionId)
        .then((d) => {
          if (!active) return;
          setSheetAvail((prev) => ({
            ...prev,
            [key]: d ? 'available' : 'missing',
          }));
          if (d) tickOption(key);
          else clearOption(key);
        })
        .catch(() => {
          if (!active) return;
          setSheetAvail((prev) => ({ ...prev, [key]: 'missing' }));
          clearOption(key);
        });
    };

    check('insights', `insights:${sessionId}`, getAgentInsights);
    check('bridge', `ebitda-bridge:${sessionId}`, getAgentEbitdaBridge);
    return () => {
      active = false;
    };
  }, [exportPanelOpen, sessionId]);

  // Pin the panel to the trigger's right-middle. Re-measured whenever
  // anything moves the trigger — scrolling the chat stream, resizing the
  // window, or re-opening the panel. Functional setter + equality guard
  // skips the re-render when nothing has actually changed, so the
  // listener stays cheap.
  useLayoutEffect(() => {
    if (!exportPanelMounted) return;
    const update = () => {
      const el = exportTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // The trigger lives near the bottom of the card, which itself sits at
      // the bottom of the chat stream — a centered panel would routinely
      // run past the bottom of the viewport (position:fixed, so page
      // scroll can't reveal it). Clamp the anchor point so the whole
      // panel stays on screen, and steer the notch back at the trigger.
      const desired = r.top + r.height / 2;
      const panelH = exportPanelRef.current
        ? exportPanelRef.current.offsetHeight
        : 0;
      let top = desired;
      let notch = null;
      if (panelH > 0) {
        const margin = 16;
        const half = panelH / 2;
        const minTop = margin + half;
        const maxTop = window.innerHeight - margin - half;
        // Viewport shorter than the panel: pin to the top edge so the
        // header (and as much content as possible) stays reachable.
        top = maxTop < minTop ? minTop : Math.min(Math.max(desired, minTop), maxTop);
        // Notch offset from the panel's top, kept clear of the rounded
        // corners. Falls back to the CSS 50% default when unclamped.
        notch = Math.min(Math.max(desired - (top - half), 18), panelH - 18);
      }
      const left = r.right + 12;
      setTriggerPos((prev) => {
        if (prev.top === top && prev.left === left && prev.notch === notch)
          return prev;
        return { top, left, notch };
      });
    };
    update();
    // The panel's height changes after mount (preset summaries reflow,
    // error rows appear) — re-clamp whenever it resizes.
    const ro =
      typeof ResizeObserver !== 'undefined' && exportPanelRef.current
        ? new ResizeObserver(update)
        : null;
    if (ro) ro.observe(exportPanelRef.current);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [exportPanelMounted]);

  // Group accounts under their *parent* P&L category — so Payroll,
  // Advertising, Insurance, etc. roll up into a single "Operating
  // Expenses" group. Per-row badges still display the leaf category;
  // only the group header collapses.
  const groups = useMemo(() => {
    const byParent = new Map();
    for (const acct of accounts) {
      const leaf = acct.category || 'Uncategorized';
      const parent = taxonomy.parentOf(leaf);
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(acct);
    }
    // Sort accounts within each parent by leaf-category order, then by
    // source row index. Keeps subcategory blocks contiguous (all
    // Payroll rows, then all Advertising rows, …) instead of
    // interleaved by source order.
    for (const rows of byParent.values()) {
      rows.sort((a, b) => {
        const ai = taxonomy.leafOrderIndex(a.category);
        const bi = taxonomy.leafOrderIndex(b.category);
        if (ai !== bi) return ai - bi;
        return (a.row_index ?? 0) - (b.row_index ?? 0);
      });
    }
    const ordered = [];
    for (const parent of taxonomy.parentOrder) {
      if (byParent.has(parent)) {
        ordered.push({ category: parent, accounts: byParent.get(parent) });
        byParent.delete(parent);
      }
    }
    for (const [parent, rows] of byParent.entries()) {
      ordered.push({ category: parent, accounts: rows });
    }
    return ordered;
  }, [accounts, taxonomy]);

  const subtotalsByGroup = useMemo(() => {
    return groups.map((g) => {
      const totals = {};
      for (let pos = 0; pos < periods.length; pos++) {
        const period = periods[pos];
        let sum = 0;
        let any = false;
        for (const acct of g.accounts) {
          const v = periodKey(acct, period.period_index, pos);
          if (typeof v === 'number' && !isNaN(v)) {
            sum += v;
            any = true;
          }
        }
        totals[period.period_index] = any ? sum : null;
      }
      return totals;
    });
  }, [groups, periods]);

  const statusMeta = STATUS_META[reconciliation.status] || STATUS_META.unknown;
  const StatusIcon = statusMeta.icon;

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(mapping, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // No silent failure — bubble to the action row.
      setDownloadError(err.message || 'Could not copy to clipboard');
    }
  };

  const handleDownload = async () => {
    if (selectedPeriodIndices.length === 0) {
      setDownloadError('Select at least one column to export.');
      return;
    }
    if (!sessionId) {
      setDownloadError('Session ID missing — reload the page and try again.');
      return;
    }
    setDownloading(true);
    setDownloadError(null);
    setDownloadNotice(null);
    try {
      const result = await downloadAgentMappingAsExcel(
        sessionId,
        mapping,
        selectedPeriodIndices,
        enableReferencing,
        {
          includeTrends: sheetOptions.trends,
          includeInsights: sheetOptions.insights,
          includeEbitdaBridge: sheetOptions.bridge,
        },
      );
      // Success — collapse the popover so the user lands back on the
      // resting card. Errors keep the popover open so the message stays
      // visible next to the controls that caused the failure.
      setExportPanelOpen(false);
      // A ticked sheet the backend couldn't build (artifact invalidated
      // between the panel check and the click) is skipped, not fatal;
      // surface which ones were left out.
      const skipped = [];
      if (sheetOptions.trends && result?.sheets?.trends === false)
        skipped.push('Trends');
      if (sheetOptions.insights && result?.sheets?.insights === false)
        skipped.push('Insights');
      if (sheetOptions.bridge && result?.sheets?.bridge === false)
        skipped.push('EBITDA Bridge');
      if (skipped.length > 0) {
        setDownloadNotice(
          `${skipped.join(' and ')} data was missing. Exported without ${
            skipped.length === 1 ? 'that sheet' : 'those sheets'
          }.`,
        );
      } else if (sheetOptions.bridge && result?.bridgeIncluded === false) {
        // The Bridge sheet landed (it lists every identified add-back) but
        // the P&L's collapsed block is approved-only and had nothing to show.
        setDownloadNotice(
          'No approved add-backs. The P&L adjusted EBITDA block was left out; the Bridge sheet lists all identified add-backs.',
        );
      }
    } catch (err) {
      if (err instanceof SourceFileMissingError) {
        // Flip the toggle off so the user can retry without re-clicking
        // through the same failure. Keep the button enabled.
        setEnableReferencing(false);
        setDownloadError(
          'Source file no longer available — cell references turned off. Click Download again to retry without them.',
        );
      } else {
        setDownloadError(err.message || 'Download failed');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!sessionId || !hasEdits) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await patchAgentMapping(sessionId, edits);
      onMappingUpdated?.(updated);
      setEdits({});
      // The edit changed the mapping; the server dropped the cached ledger.
      // Recompute it so the tie-out tab + trust badge track the saved state.
      loadProvenance();
    } catch (err) {
      // Keep edits intact so the user can retry. The error message
      // surfaces in the action row next to Download (same span as
      // downloadError).
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!accounts.length) {
    return (
      <div className="fmc-card fmc-empty">
        <AlertTriangle size={14} />
        <span>
          No accounts were classified — the agent didn't produce a final
          mapping for this file.
        </span>
      </div>
    );
  }

  // Download trigger — shared by the Mapping and EBITDA Bridge tabs (only
  // one is mounted at a time, so both can own the ref the popover anchors
  // to). Click slides the glass panel OUT TO THE RIGHT containing the
  // column-selection presets, the cell-references toggle, and the actual
  // Download CTA. The panel itself is rendered once at card level in a
  // position:fixed anchor div so it escapes the card's overflow:hidden.
  const exportTrigger = (
    <div className={`fmc-export-wrap ${exportPanelOpen ? 'is-open' : ''}`}>
      <button
        ref={exportTriggerRef}
        type="button"
        className={`fmc-btn fmc-btn-lg fmc-btn-primary fmc-export-trigger ${downloading ? 'fmc-btn-loading' : ''}`}
        onClick={() => setExportPanelOpen((o) => !o)}
        disabled={downloading || selectedPeriodIndices.length === 0}
        aria-expanded={exportPanelOpen}
        aria-haspopup="dialog"
      >
        {downloading ? (
          <Loader2 size={14} className="fmc-spin" />
        ) : (
          <Download size={14} />
        )}
        <span className="fmc-btn-label">
          {downloading
            ? 'Generating…'
            : `Download .xlsx · ${selectedPeriodIndices.length} ${
                selectedPeriodIndices.length === 1 ? 'period' : 'periods'
              }`}
        </span>
        <ChevronUp size={14} className="fmc-export-trigger-chevron" aria-hidden />
        {downloading && <span className="fmc-btn-shimmer" aria-hidden />}
      </button>
    </div>
  );

  return (
    // Plain div — same fix we applied to ThoughtTab. The entry
    // animation (opacity:0 → 1) can leave the card stuck invisible when
    // it mounts under certain conditions (snapshot reload + live event
    // splice happening in the same render cycle, framer-motion missing
    // the deferred frame, etc.). The final mapping card MUST render
    // unconditionally for every completed run.
    <div className={`fmc-card ${embedded ? 'fmc-card--embedded' : ''}`}>
      {!embedded && (
      <div className="fmc-header">
        <div className="fmc-header-left">
          <FileSpreadsheet size={14} />
          <span className="fmc-filename">
            {mapping?.file?.filename || 'Mapping'}
          </span>
        </div>
        <div className="fmc-header-right">
          <TrustBadge
            loading={provLoading}
            error={provError}
            summary={provData?.counts}
            referencingAvailable={provData?.referencing_available}
            onJump={handleJumpToTieout}
          />
          <div className={`fmc-status fmc-status-${statusMeta.tone}`}>
            <StatusIcon size={12} />
            <span>{statusMeta.label}</span>
          </div>
        </div>
      </div>
      )}

      {!embedded && (
      <div className="fmc-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'mapping'}
          className={`fmc-tab ${activeTab === 'mapping' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('mapping')}
        >
          Mapping
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'trends'}
          className={`fmc-tab ${activeTab === 'trends' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Trends
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'insights'}
          className={`fmc-tab ${activeTab === 'insights' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          Insights
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'ebitda'}
          className={`fmc-tab ${activeTab === 'ebitda' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('ebitda')}
        >
          EBITDA Bridge
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'tieout'}
          className={`fmc-tab ${activeTab === 'tieout' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('tieout')}
        >
          Tie-Out
        </button>
      </div>
      )}

      {activeTab === 'trends' ? (
        <RatiosTab sessionId={sessionId} />
      ) : activeTab === 'insights' ? (
        <InsightsTab sessionId={sessionId} />
      ) : activeTab === 'ebitda' ? (
        <>
          <EBITDABridgeTab sessionId={sessionId} />
          {/* Same export as the Mapping tab; the popover's per-tab
              checkboxes decide which analysis sheets ride along. */}
          <div className="fmc-actions fmc-actions-lg">
            {exportTrigger}
            {(downloadNotice || (downloadError && !exportPanelOpen)) && (
              <span
                className={downloadError ? 'fmc-action-error' : 'fmc-action-hint'}
                title={downloadError || downloadNotice}
              >
                {downloadError || downloadNotice}
              </span>
            )}
          </div>
        </>
      ) : activeTab === 'tieout' ? (
        <TieOutTab
          data={provData}
          loading={provLoading}
          error={provError}
          filter={tieFilter}
          onFilterChange={setTieFilter}
          onRetry={loadProvenance}
          hasEdits={hasEdits}
        />
      ) : (
        <>
      {!['pass', 'converged'].includes(reconciliation.status) && (
        <div className="result-notice warn">
          {reconciliation.status === 'unknown'
            ? 'This mapping was never verified against source Net Income. Review before relying on these figures.'
            : `This mapping did not fully reconcile to source Net Income (status ${reconciliation.status}${
                typeof reconciliation.final_discrepancy === 'number' &&
                reconciliation.final_discrepancy
                  ? `, worst-period gap ${fmtMoney(reconciliation.final_discrepancy)}`
                  : ''
              }). Review before relying on these figures.`}
        </div>
      )}
      <div className="fmc-table-wrap">
        <table className="fmc-table">
          <thead>
            <tr>
              <th className="fmc-col-account">Account</th>
              <th className="fmc-col-category">Category</th>
              {periods.map((p) => (
                <th key={p.period_index} className="fmc-col-period">
                  {p.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <React.Fragment key={g.category}>
                <tr className="fmc-group-row">
                  <td className="fmc-group-name" colSpan={2}>
                    {g.category}
                    <span className="fmc-group-count">
                      ({g.accounts.length})
                    </span>
                  </td>
                  {periods.map((p) => (
                    <td
                      key={p.period_index}
                      className="fmc-group-total fmc-num"
                    >
                      {fmtCell(subtotalsByGroup[gi][p.period_index])}
                    </td>
                  ))}
                </tr>
                {g.accounts.map((acct) => {
                  const editedCat = edits[acct.row_index];
                  const currentCat = editedCat ?? acct.category ?? 'Uncategorized';
                  const isEdited = editedCat != null;
                  return (
                  <tr
                    key={acct.row_index}
                    className={`fmc-row ${acct.corrected ? 'fmc-corrected' : ''} ${acct.sign_flipped ? 'fmc-sign-flipped' : ''} ${isEdited ? 'fmc-edited' : ''}`}
                  >
                    <td className="fmc-account" title={acct.account_name || ''}>
                      <div className="fmc-account-name">
                        <span className="fmc-account-text">
                          {acct.account_name || <span className="fmc-muted">—</span>}
                        </span>
                        {acct.corrected && (
                          <span
                            className="fmc-corrected-tag"
                            title={`Verified correction: was a different category before reconciliation.`}
                          >
                            corrected
                          </span>
                        )}
                        {acct.sign_flipped && (
                          <span
                            className="fmc-sign-flipped-tag"
                            title={`Source value was negative; mapped as a positive magnitude (sign swapped).`}
                          >
                            sign flipped
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="fmc-category">
                      <select
                        className={`fmc-cat-combo ${isEdited ? 'edited' : ''}`}
                        value={currentCat}
                        onChange={(e) => {
                          const next = e.target.value;
                          setEdits((prev) => {
                            const out = { ...prev };
                            if (next === (acct.category ?? 'Uncategorized')) {
                              delete out[acct.row_index];
                            } else {
                              out[acct.row_index] = next;
                            }
                            return out;
                          });
                        }}
                        style={taxonomy.style(currentCat)}
                        title={`Change category from ${currentCat}`}
                      >
                        {!taxonomy.selectable.includes(currentCat) && (
                          <option value={currentCat}>{currentCat}</option>
                        )}
                        {taxonomy.selectable.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </td>
                    {periods.map((p, pos) => {
                      const v = periodKey(acct, p.period_index, pos);
                      return (
                        <td
                          key={p.period_index}
                          className={`fmc-num ${v < 0 ? 'fmc-neg' : ''}`}
                        >
                          {fmtCell(v)}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {periodBreakdowns.length > 0 && (
        <div className="fmc-recon-wrap">
          <div className="fmc-recon-scroll">
            <table className="fmc-recon-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="fmc-num">Mapped NI</th>
                  <th className="fmc-num">Benchmark NI</th>
                  <th className="fmc-num">Discrepancy</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {periodBreakdowns.map((pb, idx) => {
                  const passes = pb.passes !== false;
                  return (
                    <tr key={pb.period_index ?? idx}>
                      <td>{pb.period_display || `Period ${pb.period_index}`}</td>
                      <td className="fmc-num">{fmtMoney(pb.mapped_net_income)}</td>
                      <td className="fmc-num">{fmtMoney(pb.benchmark_value)}</td>
                      <td className={`fmc-num ${passes ? '' : 'fmc-neg'}`}>
                        {fmtMoney(pb.discrepancy)}
                      </td>
                      <td>
                        <span
                          className={`fmc-recon-badge ${passes ? 'pass' : 'fail'}`}
                        >
                          {passes ? 'PASS' : 'FAIL'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="fmc-actions fmc-actions-lg">
        <button
          type="button"
          className="fmc-btn fmc-btn-lg fmc-btn-primary"
          onClick={handleSave}
          disabled={!hasEdits || saving || !sessionId}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save changes'}
        </button>

        {/* Embedded in the unified mega-card the host owns the single
            header export; the per-statement trigger would be a second,
            narrower path to the same file. Save + Copy stay. */}
        {!embedded && exportTrigger}

        <button
          type="button"
          className="fmc-btn fmc-btn-lg fmc-btn-primary"
          onClick={handleCopyJson}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy JSON'}
        </button>
        {hasEdits && !saving && (
          <span className="fmc-action-hint">
            Unsaved edits — save first to include them in the export.
          </span>
        )}
        {(saveError || downloadNotice || (downloadError && !exportPanelOpen)) && (
          <span
            className={
              saveError || downloadError ? 'fmc-action-error' : 'fmc-action-hint'
            }
            title={saveError || downloadError || downloadNotice}
          >
            {saveError || downloadError || downloadNotice}
          </span>
        )}
      </div>

        </>
      )}

      {/* Export options popover — rendered at card level (outside the tab
          conditionals) so the Mapping and EBITDA Bridge triggers share one
          panel, and outside the actions rows so position:fixed isn't
          constrained by .fmc-card's overflow:hidden. The anchor div is
          positioned at the mounted trigger's right-middle; the inner
          .fmc-export-panel runs its open/close transitions. */}
      {!embedded && exportPanelMounted && (
        <div
          className="fmc-export-anchor"
          style={{ top: triggerPos.top, left: triggerPos.left }}
        >
          <div
            ref={exportPanelRef}
            className={`fmc-export-panel ${exportPanelVisible ? 'is-open' : ''}`}
            role="dialog"
            aria-label="Export options"
            style={
              triggerPos.notch != null
                ? { ['--fmc-export-notch-top' as any]: `${triggerPos.notch}px` }
                : undefined
            }
          >
            <PeriodPicker
              periods={periods}
              selectedIndices={selectedPeriodIndices}
              onChange={setSelectedPeriodIndices}
            />
            <div className="fmc-export-toggle-row">
              <CellReferencesToggle
                checked={enableReferencing}
                onChange={setEnableReferencing}
                disabled={downloading}
                hint="When on, the downloaded .xlsx includes a copy of the source sheet and links populated cells back to it via formulas."
              />
            </div>
            <div className="fmc-export-sheets">
              <div className="fmc-export-sheets-title">Extra sheets</div>
              {[
                { key: 'trends', label: 'Trends', avail: 'available' },
                { key: 'insights', label: 'Insights', avail: sheetAvail.insights },
                { key: 'bridge', label: 'EBITDA Bridge', avail: sheetAvail.bridge },
              ].map(({ key, label, avail }) => {
                const enabled = avail === 'available' && !downloading;
                return (
                  <label
                    key={key}
                    className={`fmc-export-sheet-row ${enabled ? '' : 'is-disabled'} ${
                      sheetOptions[key] ? 'is-checked' : ''
                    }`}
                  >
                    {/* The real input stays for semantics/keyboard but is
                        visually hidden — the global input reset strips
                        native checkbox rendering, so the box span next to
                        it is the visible control (hand-rolled, like every
                        other control in the app). */}
                    <input
                      type="checkbox"
                      className="fmc-export-sheet-check"
                      checked={sheetOptions[key]}
                      disabled={!enabled}
                      onChange={(e) =>
                        setSheetOptions((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    <span className="fmc-export-sheet-box" aria-hidden>
                      {sheetOptions[key] && <Check size={10} strokeWidth={3} />}
                    </span>
                    <span className="fmc-export-sheet-label">{label}</span>
                    {avail === 'generating' && (
                      <span className="fmc-export-sheet-note">
                        Still generating.
                      </span>
                    )}
                    {avail === 'missing' && (
                      <span className="fmc-export-sheet-note">
                        Not generated yet. Open the tab to run it.
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="fmc-export-note">
              {enableReferencing
                ? 'Each formula is checked against the source cell. The file includes a Tie-Out sheet that re-verifies itself in Excel.'
                : 'Exporting without cell links. The Tie-Out tab shows which lines are not linked.'}
            </div>
            <button
              type="button"
              className={`fmc-export-confirm ${downloading ? 'is-loading' : ''}`}
              onClick={handleDownload}
              disabled={downloading || selectedPeriodIndices.length === 0}
            >
              {downloading ? (
                <Loader2 size={14} className="fmc-spin" />
              ) : (
                <Download size={14} />
              )}
              <span>
                {downloading
                  ? 'Generating…'
                  : `Download .xlsx · ${selectedPeriodIndices.length} ${
                      selectedPeriodIndices.length === 1 ? 'period' : 'periods'
                    }`}
              </span>
              {downloading && <span className="fmc-btn-shimmer" aria-hidden />}
            </button>
            {downloadError && (
              <span
                className="fmc-export-error"
                title={downloadError}
              >
                {downloadError}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalMappingCard;
